import { mapNumber } from "@/helpers/utility/MathHelper";
import { Bezier } from "bezier-js";
import type { GraphPoint } from "./GraphPoint";

export class GraphCurve {
  pStart: GraphPoint;
  pEnd: GraphPoint;

  constructor(pStart: GraphPoint, pEnd: GraphPoint) {
    this.pStart = pStart;
    this.pEnd = pEnd;
  }

  get right() {
    return this.pEnd.x;
  }
  get left() {
    return this.pStart.x;
  }

  get bottom() {
    return this.bezier.bbox().y.max;
  }

  get top() {
    return this.bezier.bbox().y.min;
  }

  get bezierStart() {
    return this.pStart?.bezierOut ?? { x: 0, y: 0 };
  }
  get bezierEnd() {
    return this.pEnd?.bezierIn ?? { x: 0, y: 0 };
  }

  get bezier() {
    return new Bezier(
      this.pStart.x,
      this.pStart.y,
      this.pStart.x + this.bezierStart.x,
      this.pStart.y + this.bezierStart.y,
      this.pEnd.x + this.bezierEnd.x,
      this.pEnd.y + this.bezierEnd.y,
      this.pEnd.x,
      this.pEnd.y
    );
  }

  getDescription({ yFactor = 1, xMin = 0, xMax = 100 }) {
    const b = {
      x: mapNumber(this.pStart.x + this.bezierStart.x, xMin, xMax, 0, 100),
      y: this.pStart.y / yFactor + this.bezierStart.y / yFactor
    };
    const c = {
      x: mapNumber(this.pEnd.x + this.bezierEnd.x, xMin, xMax, 0, 100),
      y: this.pEnd.y / yFactor + this.bezierEnd.y / yFactor
    };
    const d = {
      x: mapNumber(this.pEnd.x, xMin, xMax, 0, 100),
      y: this.pEnd.y / yFactor
    };
    return `C${b.x} ${b.y}, ${c.x} ${c.y}, ${d.x} ${d.y}`;
  }

  valueAtX(x: number) {
    let points = [];
    if (x === this.left) {
      points[0] = this.pStart;
    } else if (x === this.right) {
      points[0] = this.pEnd;
    } else {
      const intersections = this.getTsAtX(x);
      points = intersections.map((t) => this.bezier.get(t));
    }
    if (points.length === 0) return null;
    return points[0].y;
  }

  /**
   * Returns the derivative of the curve part at X (slope)
   */
  derivativeAtX(x: number) {
    let derivatives = [];
    if (x === this.left) {
      derivatives = [this.bezier.derivative(0.001)];
    } else if (x === this.right) {
      derivatives = [this.bezier.derivative(0.999)];
    } else {
      const intersections = this.getTsAtX(x);
      derivatives = intersections.map((t) => this.bezier.derivative(t));
    }

    if (derivatives.length == 0) return null;
    if (derivatives[0].x == 0) return 0;
    return derivatives[0].y / derivatives[0].x;
  }

  /**
   * Get t of the bezier curve
   * @returns all ts of the curve that intersect at this x-value
   */
  getTsAtX(x: number) {
    // create a vertical line whose intersection will be used to determine current value:
    const line = {
      p1: {
        x: x,
        y: this.top - 1
      },
      p2: {
        x: x,
        y: this.bottom + 1
      }
    };

    // create the curve based on the selected values:
    const intersections = roots(this.bezier.points, line);
    // old code: this.bezier.lineIntersects(line);

    return intersections;
  }
}

export default GraphCurve;

// Math functions,
// taken from bezier.js utils.

type Point = { x: number; y: number };
type Line = { p1: Point; p2: Point };

function crt(v: number) {
  return v < 0 ? -Math.pow(-v, 1 / 3) : Math.pow(v, 1 / 3);
}
const pi = Math.PI;
const tau = 2 * pi;
const epsilon = 0.000001;

function approximately(a: number, b: number, precision?: number) {
  return Math.abs(a - b) <= (precision || epsilon);
}

function align(points: Point[], line: Line) {
  const tx = line.p1.x,
    ty = line.p1.y,
    a = -Math.atan2(line.p2.y - ty, line.p2.x - tx),
    d = function (v: Point) {
      return {
        x: (v.x - tx) * Math.cos(a) - (v.y - ty) * Math.sin(a),
        y: (v.x - tx) * Math.sin(a) + (v.y - ty) * Math.cos(a)
      };
    };
  return points.map(d);
}

function roots(points: Point[], line: Line) {
  line = line || { p1: { x: 0, y: 0 }, p2: { x: 1, y: 0 } };

  const order = points.length - 1;
  const aligned = align(points, line);
  const reduce = function (t: number) {
    return 0 <= t && t <= 1;
  };

  if (order === 2) {
    const a = aligned[0].y,
      b = aligned[1].y,
      c = aligned[2].y,
      d = a - 2 * b + c;
    if (d !== 0) {
      const m1 = -Math.sqrt(b * b - a * c),
        m2 = -a + b,
        v1 = -(m1 + m2) / d,
        v2 = -(-m1 + m2) / d;
      return [v1, v2].filter(reduce);
    } else if (b !== c && d === 0) {
      return [(2 * b - c) / (2 * b - 2 * c)].filter(reduce);
    }
    return [];
  }

  // see http://www.trans4mind.com/personal_development/mathematics/polynomials/cubicAlgebra.htm
  const pa = aligned[0].y,
    pb = aligned[1].y,
    pc = aligned[2].y,
    pd = aligned[3].y;

  const d = -pa + 3 * pb - 3 * pc + pd;
  let a = 3 * pa - 6 * pb + 3 * pc,
    b = -3 * pa + 3 * pb,
    c = pa;

  if (approximately(d, 0)) {
    // this is not a cubic curve.
    if (approximately(a, 0)) {
      // in fact, this is not a quadratic curve either.
      if (approximately(b, 0)) {
        // in fact in fact, there are no solutions.
        return [];
      }
      // linear solution:
      return [-c / b].filter(reduce);
    }
    // quadratic solution:
    const q = Math.sqrt(b * b - 4 * a * c),
      a2 = 2 * a;
    return [(q - b) / a2, (-b - q) / a2].filter(reduce);
  }

  // at this point, we know we need a cubic solution:

  a /= d;
  b /= d;
  c /= d;

  const p = (3 * b - a * a) / 3,
    p3 = p / 3,
    q = (2 * a * a * a - 9 * a * b + 27 * c) / 27,
    q2 = q / 2,
    discriminant = q2 * q2 + p3 * p3 * p3;

  let u1, v1, x1, x2, x3;
  if (discriminant < 0) {
    const mp3 = -p / 3,
      mp33 = mp3 * mp3 * mp3,
      r = Math.sqrt(mp33),
      t = -q / (2 * r),
      cosphi = t < -1 ? -1 : t > 1 ? 1 : t,
      phi = Math.acos(cosphi),
      crtr = crt(r),
      t1 = 2 * crtr;
    x1 = t1 * Math.cos(phi / 3) - a / 3;
    x2 = t1 * Math.cos((phi + tau) / 3) - a / 3;
    x3 = t1 * Math.cos((phi + 2 * tau) / 3) - a / 3;
    return [x1, x2, x3].filter(reduce);
  } else if (discriminant === 0) {
    u1 = q2 < 0 ? crt(-q2) : -crt(q2);
    x1 = 2 * u1 - a / 3;
    x2 = -u1 - a / 3;
    return [x1, x2].filter(reduce);
  } else {
    const sd = Math.sqrt(discriminant);
    u1 = crt(-q2 + sd);
    v1 = crt(q2 + sd);
    return [u1 - v1 - a / 3].filter(reduce);
  }
}
